import { useQuery } from "@tanstack/react-query";
import { useMemo, useCallback, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { 
  type Role, type Permission, type Resource, type Action,
  hasPermission as checkPermission, 
  canAccessResource, 
  DEFAULT_ROLE_PERMISSIONS, 
  mapLegacyRole 
} from '@shared/types/permissions';
import type { AuthUser, AuthUserResponse } from "@/types/auth";
import { getQueryFn, RateLimitedError } from "@/lib/queryClient";

// ============================================================================
// Auth Hook Return Type
// ============================================================================

interface UseAuthReturn {
  // Estado
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  // 868mbzt38 — D5. Truthy only when the auth query ended in error AND
  // there is no cached user. Lets Router render `auth-load-error` while
  // `isLoading=false` covers the in-flight branch. A 401 is converted
  // by `on401: 'returnNull'` to `user = null` without touching this.
  authError: Error | null;

  // Helpers de rol (retrocompatibilidad)
  role: Role | null;
  organizationId: string | null;

  // Helpers de permisos (NUEVO)
  permissions: Permission[];
  hasPermission: (resource: Resource, action: Action) => boolean;
  canAccess: (resource: Resource) => boolean;

  // Acciones
  refetch: () => Promise<void>;
}

// ============================================================================
// Main Hook
// ============================================================================

export function useAuth(): UseAuthReturn {
  const {
    data: rawUser,
    isLoading,
    error,
    refetch: queryRefetch,
  } = useQuery<AuthUserResponse | null>({
    queryKey: ["/api/auth/user"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    // N11 (868mn3t52): a 429 is not retried. Each retry would wait inside
    // `waitForGate` while the page stays on the skeleton; failing at once
    // lets Router show the rate-limit notice of `AuthLoadError`.
    retry: (failureCount, err) => !(err instanceof RateLimitedError) && failureCount < 2,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 3000),
    // [SA-33 / 868kbjw74 EX-03] Periodic refetch so a freshly revoked
    // permission (e.g. userManagement) propagates to the sidebar without
    // a hard reload. 60s is light enough to keep the auth endpoint
    // uncontended; revocations are infrequent.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  // 868mbzt38 — D5 / CA-04. The error is exposed when no cached user
  // exists. TanStack leaves `data` as `undefined` on initial failure, not
  // as `null` — `null` is reserved for the `on401: 'returnNull'` path. We
  // check `rawUser === undefined` so the auth-load-error notice renders
  // for a 429, 500, or network failure that happens during the initial
  // fetch OR a poll after the user was never signed in.
  const authError: Error | null =
    error instanceof Error && rawUser === undefined ? error : null;

  // Transform raw response to AuthUser with proper typing
  const user = useMemo<AuthUser | null>(() => {
    if (!rawUser) {
      return null;
    }
    
    // Mapear roles legacy (unit_owner -> owner)
    const role = mapLegacyRole(rawUser.role);
    
    return {
      id: rawUser.id,
      email: rawUser.email,
      firstName: rawUser.firstName ?? null,
      lastName: rawUser.lastName ?? null,
      phone: rawUser.phone ?? null,
      // [868kgnkfu] Pass the persisted language through; consumers that
      // compare `=== 'es'` keep working for both 'en' and null.
      language: rawUser.language,
      profileImageUrl: rawUser.profileImageUrl ?? null,
      role,
      // organizationId ya viene del BE
      organizationId: rawUser.organizationId,
      // Usar permisos del BE si existen, sino usar defaults del rol
      permissions: rawUser.permissions ?? DEFAULT_ROLE_PERMISSIONS[role],
      isActive: rawUser.isActive ?? true,
      createdAt: rawUser.createdAt,
      updatedAt: rawUser.updatedAt,
    };
  }, [rawUser]);

  // Sync Sentry user context on auth state change (S5-BE-04)
  useEffect(() => {
    if (user) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- REASON: Sentry User type doesn't include custom fields (organizationId, role) natively
      Sentry.setUser({ id: user.id, organizationId: user.organizationId, role: user.role } as any);
    } else {
      Sentry.setUser(null);
    }
  }, [user]);

  // Derived values
  const isAuthenticated = useMemo(() => !!user, [user]);
  const role = useMemo(() => user?.role ?? null, [user]);
  const organizationId = useMemo(() => user?.organizationId ?? null, [user]);
  const permissions = useMemo(() => user?.permissions ?? [], [user]);

  // Permission helpers
  const hasPermission = useCallback(
    (resource: Resource, action: Action): boolean => {
      if (!user) {return false;}
      return checkPermission(permissions, resource, action);
    },
    [user, permissions]
  );

  const canAccess = useCallback(
    (resource: Resource): boolean => {
      if (!user) {return false;}
      return canAccessResource(permissions, resource);
    },
    [user, permissions]
  );

  // Refetch wrapper
  const refetch = useCallback(async () => {
    await queryRefetch();
  }, [queryRefetch]);

  return {
    user,
    isLoading,
    isAuthenticated,
    authError,
    role,
    organizationId,
    permissions,
    hasPermission,
    canAccess,
    refetch,
  };
}

// ============================================================================
// Convenience Hooks
// ============================================================================

/**
 * Hook simplificado para verificar permisos
 * @example
 * const canCreateProperty = usePermission('properties', 'create');
 */
export function usePermission(resource: Resource, action: Action): boolean {
  const { hasPermission } = useAuth();
  return hasPermission(resource, action);
}

/**
 * Hook para verificar acceso a un recurso (cualquier acción)
 * @example
 * const canAccessReports = useResourceAccess('reports');
 */
export function useResourceAccess(resource: Resource): boolean {
  const { canAccess } = useAuth();
  return canAccess(resource);
}

/**
 * Hook para obtener el organizationId del usuario actual
 * Útil para queries que requieren filtrar por organización
 */
export function useOrganizationId(): string | null {
  const { organizationId } = useAuth();
  return organizationId;
}

/**
 * Hook para obtener el rol del usuario actual
 */
export function useRole(): Role | null {
  const { role } = useAuth();
  return role;
}

// Re-export types for convenience
export type { Role, Permission, Resource, Action } from '@shared/types/permissions';
export type { AuthUser } from '@/types/auth';
